import { gql, useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty, omit } from 'lodash'
import { useEffect, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'

import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { Content, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { NewLine } from 'features/Lines/components/NewLine'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { messages as fileMessages } from 'messages/file'
import { usePushFileToS3 } from 'utils/file'
import { getDifference } from 'utils/object'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'

const messages = defineMessages({
  helmet: {
    id: 'lines.edit.helmet',
    defaultMessage: 'Edit Line | Nexbus Admin'
  }
})

const EDIT_LINE = gql`
  mutation EditLine($line: updateCompanyLineInput!) {
    updateCompanyLine(input: $line)
  }
`

const GET_LINE = gql`
  query GetLine($lineId: ID!) {
    companyLine(input: { ID: $lineId }) {
      id
      name
      amenities
      refundRules
      class
      type
      logoPath
    }
  }
`

const EditLineView = (): TSFixMe => {
  const navigate = useNavigate()
  const { lineId } = useParams()
  const intl = useIntl()
  const login = useLogin()
  const [s3Error, setS3Error] = useState()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  let content

  const [editLine, { data: mutationData, loading: mutationLoading, error: mutationError }] = useMutation(EDIT_LINE)
  const pushFileToS3 = usePushFileToS3()

  const handleSubmit = async (line: TSFixMe): Promise<TSFixMe> => {
    // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
    setS3Error()
    const formData = omit(line, ['logo'])
    const payload = getDifference(formData, queryData.companyLine)

    if (line.logo !== queryData.companyLine.logoPath) {
      try {
        payload.logoPath = await pushFileToS3(line.logo)
      } catch (ex) {
        // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
        setS3Error(intl.formatMessage(fileMessages.uploadFileError, { count: 1 }))
        return
      }
    }

    if (isEmpty(payload)) {
      navigate('/lines')
    } else {
      editLine({
        variables: {
          line: {
            id: queryData.companyLine.id,
            ...payload
          }
        }
      })
    }
  }

  useEffect((): TSFixMe => {
    if (mutationData) {
      navigate('/lines')
    }
  }, [mutationData])

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery(GET_LINE, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      lineId
    }
  })

  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (!canWrite(FEATURES.COMPANY_LINE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.COMPANY_LINE} />
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="lines.edit.error.description"
              defaultMessage="There was an error while loading your line"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button onClick={(): TSFixMe => refetch()} kind="primary">
              <FormattedMessage id="lines.edit.error.action" defaultMessage="Reload your line" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <NewLine
        onSubmit={handleSubmit}
        line={queryData.companyLine}
        loading={mutationLoading}
        error={mutationError}
        s3Error={s3Error}
        isEdit
      />
    )
  }

  return (
    // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
    <Content hasSidebar hasHeader hasMaxWidth>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="lines.edit.title" defaultMessage="Edit line" />
        </Heading.H3>
      </ContentHeader>

      <section>
        <Card.Container>{content}</Card.Container>
      </section>
    </Content>
  )
}

export default withLastUrl(EditLineView)
